import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { TWclassNames } from "../../../utils/Div";
import {
  collectGuideEffects,
  normalizeGuideTemplate,
} from "./caseGuideUtils";
import {
  getCurrentQuestion,
  getNextQuestionNodeId,
  getQuestionNodes,
  getStartNodeId,
  getVisitedQuestionNodes,
  resolveGuideQuestionNavigation,
} from "../../../utils/guideFlow/guideFlowCore";

export default function CaseGuideOnboardingDialog({
  open,
  template,
  onClose,
  onComplete,
  purpose = "onboarding",
}) {
  const { t } = useTranslation();
  const normalizedTemplate = useMemo(() => normalizeGuideTemplate(template), [template]);
  const flow = normalizedTemplate?.settings?.flow;
  const questions = useMemo(() => getQuestionNodes(flow), [flow]);
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [history, setHistory] = useState([]);
  const [answers, setAnswers] = useState({});
  const [reviewSummaryOpen, setReviewSummaryOpen] = useState(false);

  const currentQuestionNode = getCurrentQuestion(flow, currentNodeId);
  const currentQuestion = currentQuestionNode?.data;
  const currentQuestionKey = currentQuestion?.key || currentQuestionNode?.id;
  const currentValue = answers[currentQuestionKey];
  const canContinue =
    !currentQuestion?.required ||
    (Array.isArray(currentValue)
      ? currentValue.length > 0
      : currentValue !== undefined && currentValue !== "");
  const { nextQuestionNodeId } =
    canContinue && currentQuestionNode
      ? resolveGuideQuestionNavigation(flow, currentQuestionNode, currentValue, answers)
      : { nextQuestionNodeId: null };
  const isLastStep =
    Boolean(currentQuestionNode) && canContinue && nextQuestionNodeId === null;
  const visitedQuestions = useMemo(() => getVisitedQuestionNodes(flow, answers), [flow, answers]);
  const currentIndex = currentNodeId ? Math.max(0, visitedQuestions.findIndex((node) => node.id === currentNodeId)) : 0;
  const progress = questions.length
    ? reviewSummaryOpen
      ? 100
      : ((currentIndex + 1) / questions.length) * 100
    : 0;
  const effects = useMemo(() => collectGuideEffects(normalizedTemplate, answers), [normalizedTemplate, answers]);

  useEffect(() => {
    if (!open) return;
    const activeFlow = normalizedTemplate?.settings?.flow;
    setCurrentNodeId(getNextQuestionNodeId(activeFlow, getStartNodeId(activeFlow), {}));
    setHistory([]);
    setAnswers({});
    setReviewSummaryOpen(false);
  }, [open, normalizedTemplate?.id, normalizedTemplate?.updated_at]);

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const toggleMultiValue = (key, value) => {
    const current = Array.isArray(answers[key]) ? answers[key] : [];
    setAnswer(
      key,
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    );
  };

  const complete = () => {
    onComplete?.({
      case_guide_template_id: normalizedTemplate.id,
      purpose,
      answers,
      effects,
    });
  };

  const close = () => {
    setCurrentNodeId(getNextQuestionNodeId(flow, getStartNodeId(flow), {}));
    setHistory([]);
    setAnswers({});
    setReviewSummaryOpen(false);
    onClose?.();
  };

  const goBack = () => {
    if (reviewSummaryOpen) {
      setReviewSummaryOpen(false);
      return;
    }
    if (history.length === 0) {
      close();
      return;
    }

    const previousNodeId = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setCurrentNodeId(previousNodeId);
  };

  const goNext = () => {
    if (reviewSummaryOpen) {
      complete();
      return;
    }
    if (isLastStep) {
      setReviewSummaryOpen(true);
      return;
    }

    setHistory((prev) => [...prev, currentNodeId]);
    setCurrentNodeId(nextQuestionNodeId);
  };

  return (
    <PMDialog open={open} onClose={close} size="4xl" title={normalizedTemplate?.title || "Case guide"} zIndex={3000} panelClassName="p-0 overflow-hidden">
      <div className="flex max-h-[90vh] min-h-[560px] flex-col bg-slate-950 text-white">
        <div className="relative overflow-hidden px-6 py-6">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/25 via-blue-500/10 to-purple-500/25" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-emerald-100 ring-1 ring-white/20">
                <SparklesIcon className="h-4 w-4" />
                {purpose === "request_info" ? t("Etterspørre info") : t("Guidet case-opprettelse")}
              </div>
              <h2 className="mt-4 text-2xl font-semibold">{normalizedTemplate?.title || t("Case guide")}</h2>
              {normalizedTemplate?.description && (
                <p className="mt-2 max-w-2xl text-sm text-slate-200">{normalizedTemplate.description}</p>
              )}
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-3 text-right ring-1 ring-white/20">
              <p className="text-xs text-slate-300">{reviewSummaryOpen ? t("Oppsummering") : t("Steg")}</p>
              <p className="text-lg font-semibold">
                {questions.length
                  ? reviewSummaryOpen
                    ? `${questions.length}/${questions.length}`
                    : `${currentIndex + 1}/${questions.length}`
                  : "0/0"}
              </p>
            </div>
          </div>
          <div className="relative mt-6 h-2 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-emerald-400 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white text-gray-900">
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            {reviewSummaryOpen ? (
              <CaseGuideSummaryPanel answers={answers} effects={effects} t={t} visitedQuestions={visitedQuestions} />
            ) : !currentQuestion ? (
              <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-500">
                {t("Ingen guide-spørsmål er konfigurert.")}
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium uppercase tracking-wide text-emerald-700">{t("Spørsmål")}</p>
                <h3 className="mt-2 text-2xl font-semibold text-gray-950">{currentQuestion.label}</h3>
                {currentQuestion.help_text && <p className="mt-2 text-sm text-gray-500">{currentQuestion.help_text}</p>}

                <div className="mt-6">
                  {["single_select", "multi_select"].includes(currentQuestion.answer_type) && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {(currentQuestion.options || []).map((option) => {
                        const selected =
                          currentQuestion.answer_type === "multi_select"
                            ? Array.isArray(currentValue) && currentValue.includes(option.value)
                            : currentValue === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() =>
                              currentQuestion.answer_type === "multi_select"
                                ? toggleMultiValue(currentQuestionKey, option.value)
                                : setAnswer(currentQuestionKey, option.value)
                            }
                            className={TWclassNames(
                              "rounded-2xl border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md",
                              selected ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-gray-200 bg-white"
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <span className="font-medium text-gray-950">{option.label}</span>
                              {selected && <CheckCircleIcon className="h-5 w-5 text-emerald-600" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {currentQuestion.answer_type === "boolean" && (
                    <div className="grid grid-cols-2 gap-3">
                      {[true, false].map((value) => (
                        <button
                          key={String(value)}
                          type="button"
                          onClick={() => setAnswer(currentQuestionKey, value)}
                          className={TWclassNames(
                            "rounded-2xl border p-5 text-center font-medium",
                            currentValue === value ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-gray-200"
                          )}
                        >
                          {value ? t("Ja") : t("Nei")}
                        </button>
                      ))}
                    </div>
                  )}

                  {["text", "date"].includes(currentQuestion.answer_type) && (
                    <input
                      type={currentQuestion.answer_type === "date" ? "date" : "text"}
                      value={currentValue || ""}
                      onChange={(e) => setAnswer(currentQuestionKey, e.target.value)}
                      className="block w-full rounded-xl border-gray-300 text-sm shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  )}

                  {currentQuestion.answer_type === "textarea" && (
                    <textarea
                      rows={5}
                      value={currentValue || ""}
                      onChange={(e) => setAnswer(currentQuestionKey, e.target.value)}
                      className="block w-full rounded-xl border-gray-300 text-sm shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 bg-white p-4 text-gray-900">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {history.length === 0 && !reviewSummaryOpen ? t("Avbryt") : t("Tilbake")}
          </button>
          <button
            type="button"
            disabled={reviewSummaryOpen ? questions.length === 0 : !canContinue || questions.length === 0}
            onClick={goNext}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {reviewSummaryOpen ? t("Bruk guide") : isLastStep ? t("guide.case_view_summary") : t("Neste")}
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </PMDialog>
  );
}

function CaseGuideSummaryPanel({ visitedQuestions, answers, effects, t }) {
  return (
    <div>
      <h3 className="text-xl font-semibold text-gray-950">{t("Oppsummering")}</h3>
      <p className="mt-2 text-sm text-gray-600">{t("guide.case_summary_review_intro")}</p>
      <div className="mt-6 space-y-3">
        {visitedQuestions.map((questionNode) => {
          const question = questionNode.data || {};
          const key = question.key || questionNode.id;
          return (
            <div key={questionNode.id} className="rounded-xl border border-gray-100 bg-gray-50 p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{question.label}</p>
              <p className="mt-2 text-sm text-gray-900">{formatAnswer(question, answers[key]) || "–"}</p>
            </div>
          );
        })}
      </div>
      {effects.tag_ids?.length > 0 ? (
        <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          {t("Guiden foreslår")} {effects.tag_ids.length} {t("etiketter")}
        </div>
      ) : null}
      {effects.next_case_guide_template_id ? (
        <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
          {t("guide.case_chain_summary_hint")}
        </div>
      ) : null}
    </div>
  );
}

function formatAnswer(question, value) {
  if (value === undefined || value === null || value === "") return "";
  const options = question.options || [];
  if (Array.isArray(value)) {
    return value
      .map((item) => options.find((option) => option.value === item)?.label || item)
      .join(", ");
  }
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  return options.find((option) => option.value === value)?.label || value;
}
